import { useEffect, useState } from "react";
import { IFormSection, TSubmitType } from "../../types/common";
import {
  API_FIELD_OFFICERS,
  API_INVENTORY_ALL,
  API_LOAN_APPLICATIONS,
  API_LOAN_APPLICATIONS_DRAFTS,
  API_PERIOD_CONFIGURABLES,
} from "../../utils/apis";

import {
  beautify,
  createSelectItem,
  displayDocuments,
  getAndPrepareFiles,
  getResultOptions,
  prepareObjectArrayFormdataPost,
  prepareObjectFormdataPost,
  removeUnnecessaryFields,
  returnObject,
} from "../../utils/helpers";
import { useGetOwnerDetails } from "../../utils/owner_details";
import {
  URL_LOAN_APPLICATIONS,
  URL_LOAN_APPLICATIONS_DRAFTS,
} from "../../utils/urls";
// import { useLocationDataForm } from "../location_data/useLocationDataForm";
import useGet from "../common/useGet";
import {
  DISBURSED,
  EXISTING_APPLICANT,
  FORM_DATA,
  GENDER_SELECTIONS,
  MIGRATED_APPLICANT,
  MODAL,
  NEW_APPLICANT,
  RESTRUCTURED_APPLICANT,
} from "../../utils/constants";
import { Add } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import DetailsCard from "../../components/common/DetailsCards";

export type TFormCustomProps = {
  id?: number;
  submitType?: TSubmitType;
};

const getPreppedPayload = (payload: any) => {
  const files = [
    "recommendation_letter",
    "national_id",
    "passport_photo",
    "guarantor_id",
    "letter",
    "local_council_chairperson_letter",
  ];
  let preppedPayload = getAndPrepareFiles(payload, files);
  preppedPayload = prepareObjectFormdataPost(
    preppedPayload,
    "local_council_chairperson",
  );
  preppedPayload = prepareObjectFormdataPost(
    preppedPayload,
    "stage_chairperson",
  );
  preppedPayload = prepareObjectFormdataPost(
    preppedPayload,
    "new_field_officer",
  );
  preppedPayload = prepareObjectArrayFormdataPost(preppedPayload, "guarantors");
  return preppedPayload;
};

export const useLoanApplicationForm = ({
  form,
  updateFormValues,
  error,
  setError,
  updateFormValue,
}) => {
  // const location = useLocationDataForm(form);
  const { user } = useAuth();
  const [optional, setOptional] = useState(["email"]);
  const ownerDetails = useGetOwnerDetails({
    form,
    updateFormValues,
    error,
    setError,
    optional,
  });
  const [interestPercent, setInterestPercent] = useState(1);
  const [numberPlate, setNumberPlate] = useState(form?.number_plate || "");
  const [applicationInfo, setApplicationInfo] = useState<any>({});
  const { loadData: fetchLoanApplication, data: loanApplicationData } =
    useGet();
  const {
    loadData: getExistingLoanApplication,
    data: existingLoanApplicationData,
    success: existingLoanApplicationSuccess,
  } = useGet();

  const { fields: loanPeriodFields, options: loanPeriodOptions } =
    useLoanPeriodFields({
      form,
      updateFormValue,
    });

  const hasNoId: boolean =
    form.application_type === RESTRUCTURED_APPLICANT
      ? false
      : form?.id === undefined;
  const isOptionalApplicant =
    [MIGRATED_APPLICANT, RESTRUCTURED_APPLICANT].includes(
      form?.application_type,
    ) && !hasNoId;

  useEffect(() => {
    if (
      isOptionalApplicant ||
      form.application_type === RESTRUCTURED_APPLICANT
    ) {
      setOptional(["email", "nin"]);
    } else {
      setOptional(["email"]);
    }
  }, [form?.application_type]);

  const isRequired = !(
    (isOptionalApplicant && !hasNoId) ||
    form?.application_type === RESTRUCTURED_APPLICANT
  );

  const shouldFetchNumberPlate = () =>
    // !form.id &&
    [EXISTING_APPLICANT, RESTRUCTURED_APPLICANT].includes(
      form.application_type,
    );

  const getLoanApplication = (newNumberPlate: string) => {
    if (shouldFetchNumberPlate() && numberPlate) {
      fetchLoanApplication({
        api: `${API_LOAN_APPLICATIONS}`,
        params: { number_plate: newNumberPlate || numberPlate },
      });
    }
  };
  useEffect(() => {
    if (form.nin && hasNoId) {
      if (form?.nin?.length == 14) {
        getExistingLoanApplication({
          api: `${API_LOAN_APPLICATIONS}`,
          params: { nin: form?.nin },
        });
      }
    }
  }, [form.nin]);

  useEffect(() => {
    if (
      existingLoanApplicationSuccess &&
      existingLoanApplicationData?.count > 0
    ) {
      toast.error("User with this NIN already has a loan application");
      setError({
        nin: "User with this NIN already has a loan application",
        notSubmit: true,
      });
    }
    if (
      existingLoanApplicationSuccess &&
      existingLoanApplicationData?.count === 0
    ) {
      setError({});
    }
  }, [existingLoanApplicationSuccess, existingLoanApplicationData]);

  useEffect(() => {
    if (
      loanApplicationData &&
      loanApplicationData.results?.length > 0 &&
      form.application_type === EXISTING_APPLICANT
    ) {
      const existingApplication = loanApplicationData.results[0];
      // remove unnecessary fields
      delete existingApplication.id;
      delete existingApplication.status;
      delete existingApplication.created_at;
      delete existingApplication.updated_at;
      delete existingApplication.updated_by;
      delete existingApplication.created_by;
      delete existingApplication.loan_application_attachments;
      delete existingApplication.owner;
      delete existingApplication.payment_status;
      // delete loan particulars
      delete existingApplication.loan_type;
      delete existingApplication.loan_validity_period;
      delete existingApplication.number_of_months;
      delete existingApplication.amortization_method;
      delete existingApplication.repayment_period;
      delete existingApplication.interest_percent;
      delete existingApplication.interest;
      delete existingApplication.total_loan_value;
      delete existingApplication.amount;
      delete existingApplication.deposit_paid;
      delete existingApplication.disbursement_date;
      delete existingApplication.system_instance;
      // delete product details
      delete existingApplication.product;
      delete existingApplication.declaration;
      delete existingApplication.deleted;
      // delete vehicle details
      delete existingApplication.vehicle_type;
      delete existingApplication.model;
      delete existingApplication.vehicle_type_name;
      delete existingApplication.vehicle_identification_number;
      delete existingApplication.make_and_color;

      // update number plate
      // existingApplication.number_plate = numberPlate;
      existingApplication.application_type = EXISTING_APPLICANT;
      updateFormValues(existingApplication);
    }
    if (
      form.application_type === RESTRUCTURED_APPLICANT &&
      loanApplicationData?.results?.length > 0
    ) {
      updateFormValue(
        "previous_loan_application",
        loanApplicationData.results[0]?.id,
      );
      setApplicationInfo({
        ...removeUnnecessaryFields(loanApplicationData.results[0], [
          "id",
          "status",
          "created_at",
          "updated_at",
          "updated_by",
          "created_by",
          "total_amount_payable",
        ]),
      });
    }
  }, [loanApplicationData]);

  useEffect(() => {
    updateFormValue("interest_percent", interestPercent);
  }, [interestPercent]);

  const DocumentsList = (docsList: any) => {
    return (
      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          padding: "10px 0",
        }}
      >
        {Object.entries(docsList).map(([key]) => (
          <div
            key={key}
            style={{
              display: "flex",
              gap: 5,
              flexDirection: "column",
              background: "#f5f5f5",
              borderRadius: 8,
              padding: 12,
              border: "1px solid #eee",
            }}
          >
            <span
              className="font12"
              style={{
                color: "#666",
                fontWeight: 500,
              }}
            >
              {beautify(key)}
            </span>
            <span>{docsList[key]}</span>
          </div>
        ))}
      </div>
    );
  };

  const sections: IFormSection[] = [
    {
      title: "Previous Loan Information",
      visible:
        [EXISTING_APPLICANT, RESTRUCTURED_APPLICANT].includes(
          form.application_type,
        ) && form?.previous_loan_application !== undefined,
      fields: [
        {
          field_name: "application_info",
          type: "component",
          label: "Application Info",
          props: {
            wide: true,
          },
          component: (
            <div>
              <DetailsCard viewData={applicationInfo} />
            </div>
          ),
        },
      ],
    },
    {
      title: "Personal Information",
      fields: [
        {
          field_name: "application_type",
          type: "select",
          label: "Applicant Type",
          options_data: [
            ...[
              NEW_APPLICANT,
              EXISTING_APPLICANT,
              MIGRATED_APPLICANT,
              RESTRUCTURED_APPLICANT,
            ].map((item) => createSelectItem(item)),
          ],
          props: {
            required: true,
          },
        },
        {
          field_name: "number_plate",
          type: "select",
          label: "Number Plate",
          props: {
            required: isRequired,
            onInput: (e) => setNumberPlate(e.target.value),
            onChange: (value) => {
              updateFormValue("number_plate", value);
              getLoanApplication(value);
            },
          },
          options_name: "number_plates",
          visible: [EXISTING_APPLICANT, RESTRUCTURED_APPLICANT].includes(
            form.application_type,
          ),
        },
        ...ownerDetails,
        {
          field_name: "residential_address",
          type: "textarea",
          label: "Residential Address",
          props: { required: isRequired, wide: true },
        },
      ],
    },
    // {
    //   title: "Location Details",
    //   fields: [...location?.fields(isRequired)],
    // },
    {
      title: "Employment Details",
      fields: [
        {
          field_name: "category",
          type: "select",
          label: "Category",
          options_data: [
            { id: "Stage", name: "Stage" },
            { id: "Work", name: "Work" },
          ],
          props: { required: isRequired },
        },
        {
          field_name: "stage_name",
          type: "text",
          label: "Stage Name",
          visible: form.category === "Stage",
        },
        {
          field_name: "stage_id",
          type: "number",
          label: "Stage ID",
          visible: form.category === "Stage",
        },
        {
          field_name: "year_joined",
          type: "number",
          label: "Year Joined",
          visible: form.category === "Stage",
          props: { required: isRequired },
        },
        {
          field_name: "work_id",
          type: "text",
          label: "Work ID",
          visible: form.category === "Work",
        },
      ],
    },
    {
      title: "Stage Chairperson Details",
      visible: form.category === "Stage",
      fields: [
        {
          field_name: "stage_chairperson",
          type: MODAL,
          label: "Add Stage Chairperson",
          props: {
            required: isRequired,
            multiple: false,
            icon: <Add fontSize="small" />,
            wide: true,
          },
          fields: [
            {
              field_name: "first_name",
              type: "text",
              label: "first Name",
              props: { required: isRequired },
            },
            {
              field_name: "last_name",
              type: "text",
              label: "last Name",
              props: { required: isRequired },
            },
            {
              field_name: "other_name",
              type: "text",
              label: "Other Name",
            },
            {
              field_name: "phone_number",
              type: "tel",
              label: "Phone Number",
              props: { required: isRequired },
            },
            {
              field_name: "physical_address",
              type: "textarea",
              label: "Physical Address",
            },
            // {
            //   field_name: "loan_application_stage_chairperson_attachments",
            //   type: "file",
            //   label: "Letter",
            //   props: { nested_attachment: true },
            // },
          ],
        },
      ],
    },
    {
      title: " LC1 Chairperson's Details",
      fields: [
        {
          field_name: "local_council_chairperson",
          type: MODAL,
          label: "LC1 Chairperson",
          props: {
            required: isRequired,
            multiple: false,
            icon: <Add fontSize="small" />,
            wide: true,
          },
          fields: [
            {
              field_name: "first_name",
              type: "text",
              label: "First Name",
              props: { required: isRequired },
            },
            {
              field_name: "last_name",
              type: "text",
              label: "Last Name",
              props: { required: isRequired },
            },
            {
              field_name: "other_name",
              type: "text",
              label: "Other Name",
            },
            {
              field_name: "phone_number",
              type: "tel",
              label: "Telephone Number",
              props: { required: isRequired },
            },
            {
              field_name: "address",
              type: "textarea",
              label: "Physical Address",
            },
            // {
            //   field_name: "local_council_chairperson_letter",
            //   type: "file",
            //   label: "Letter",
            //   props: { nested_attachment: true },
            // },
          ],
        },
      ],
    },
    {
      title: "Guarantor Details",
      fields: [
        {
          field_name: "guarantors",
          type: MODAL,
          label: "Add Guarantor",
          props: {
            required: isRequired,
            icon: <Add fontSize="small" />,
            wide: true,
          },
          fields: [
            {
              field_name: "first_name",
              type: "text",
              label: "first Name",
              props: { required: true },
            },
            {
              field_name: "last_name",
              type: "text",
              label: "last Name",
              props: { required: true },
            },
            {
              field_name: "profession",
              type: "text",
              label: "Occupation",
            },

            {
              field_name: "phone_number",
              type: "tel",
              label: "Phone Number",
              props: { required: true },
            },
            // {
            //   field_name: "national_id",
            //   type: "file",
            //   label: "Guarantor ID",
            //   props: { nested_attachment: true },
            // },
            {
              field_name: "physical_address",
              type: "textarea",
              label: "Physical Address",
              props: { required: true },
            },
          ],
        },
      ],
    },
    {
      title: "Loan Particulars",
      fields: [
        {
          field_name: "loan_type",
          type: "select",
          label: "Loan Type",
          props: { required: isRequired, smallWidth: true },
          options_name: "interests",
        },
        ...loanPeriodFields,
        {
          field_name: "preferred_inventory",
          type: "select",
          label: "Preferred Inventory",
          props: { smallWidth: true },
          options_name: "inventories",
        },

        // {
        //   field_name: "amount",
        //   type: "number",
        //   label: "Loan Amount",
        //   props: {
        //     required: true,
        //     disabled: form?.status == "approved" || form?.status == "disbursed",
        //   },
        // },
        {
          field_name: "interest_percent",
          type: "number",
          label: "Default Interest Rate",
          props: { disabled: true, smallWidth: true },
          visible: form?.loan_type,
        },
        {
          field_name: "interest_rate",
          type: "select",
          label: "Custom Interest Rate",
          props: { smallWidth: true },
          options_name: "interest_rates",
          visible: form?.loan_type,
        },
        {
          field_name: "restructured_loan_value",
          type: "number",
          label: "Restructured Loan Value",
          visible: form?.application_type === RESTRUCTURED_APPLICANT,
        },
        {
          field_name: "deposit_paid",
          label: "Down Payment",
          visible: form?.application_type === RESTRUCTURED_APPLICANT,
        },
        {
          field_name: "field_officer",
          label: "Field Officer",
          type: "select",
          props: {
            required: form?.new_field_officer ? false : isRequired,
            acceptNewItems: true,
            newLine: true,
          },
          options_name: "field_officers",
          visible: form?.loan_type,
          action_modal: {
            label: "Add Field Officer",
            field_name: "new_field_officer",
            props: { multiple: false },
            fields: [
              {
                field_name: "first_name",
                type: "text",
                label: "First Name",
                props: { required: true },
              },
              {
                field_name: "last_name",
                type: "text",
                label: "Last Name",
                props: { required: true },
              },
              {
                field_name: "gender",
                type: "select",
                label: "Gender",
                props: { required: true },
                options_data: GENDER_SELECTIONS,
              },
              {
                field_name: "phone_number",
                type: "tel",
                label: "Phone Number",
              },
            ],
          },
        },
        // {
        //   field_name: "total_loan_value",
        //   type: "number",
        //   label: "Estimated Total Amount Payable",
        //   props: { required: true, disabled: true },
        // },
      ],
    },
    {
      title: "Supporting Documents",
      fields: [
        ...returnObject(!hasNoId, [
          {
            field_name: "documents",
            type: "component",
            label: "Documents",
            props: { wide: true },
            component: DocumentsList(
              displayDocuments(form?.loan_application_attachments),
            ),
          },
        ]),
        {
          field_name: "national_id",
          type: "file",
          label: "National ID",
          props: { required: hasNoId },
        },
        {
          field_name: "passport_photo",
          type: "file",
          label: "Passport Photo",
        },
        {
          field_name: "local_council_chairperson_letter",
          type: "file",
          label: "Chairperson Recommendation Letter",
          props: {
            parent_field: "local_council_chairperson",
            required: form?.id
              ? false
              : form?.local_council_chairperson
                    ?.local_council_chairperson_letter
                ? false
                : hasNoId,
          },
        },
        {
          field_name: "national_id",
          type: "file",
          label: "Guarantor ID 1",
          props: {
            parent_field: "guarantors",
            multiple: true,
            parent_index: 0,
            newLine: true,
            smallWidth: true,
          },
        },
        {
          field_name: "national_id",
          type: "file",
          label: "Guarantor ID 2",
          props: {
            parent_field: "guarantors",
            multiple: true,
            parent_index: 1,
            smallWidth: true,
          },
        },
        {
          field_name: "national_id",
          type: "file",
          label: "Guarantor ID 3",
          props: {
            parent_field: "guarantors",
            multiple: true,
            parent_index: 2,
            smallWidth: true,
          },
        },
        {
          field_name: "loan_application_stage_chairperson_attachments",
          type: "file",
          label: "Stage Chairperson Letter",
          props: {
            parent_field: "stage_chairperson",
          },
          visible: form.category === "Stage",
        },
      ],
    },
    {
      title: "Declaration",
      fields: [
        {
          field_name: "user_const",
          type: "component",
          component: (
            <div className="font12 capitalize">
              submitted by:{" "}
              <b>
                {user?.first_name} {user?.last_name}
              </b>
            </div>
          ),
        },
        {
          field_name: "declaration",
          type: "checkbox",
          label:
            "I declare that the information provided above is true and correct to the best of my knowledge",
          props: { required: true, wide: true },
        },
      ],
    },
  ];

  const redirect = `${URL_LOAN_APPLICATIONS}/submitted`;
  const draftRedirect = URL_LOAN_APPLICATIONS_DRAFTS;
  const draftsApi = API_LOAN_APPLICATIONS_DRAFTS;
  const getConfigOptions = () => {
    const { loadData: getInterests, data: interests } = useGet();
    const { loadData: getInterestRates, data: interestRates } = useGet();
    const { loadData: getNumberPlates, data: numberPlates } = useGet();
    const { loadData: getInventories, data: inventories } = useGet();
    const { loadData: getFieldOfficers, data: fieldOfficers } = useGet();
    useEffect(() => {
      getInterests({
        api: "configurables/interest-configurations/",
        params: { default: true },
      });

      getInventories({
        api: API_INVENTORY_ALL,
        params: {
          category_name: "loan",
        },
      });
      getFieldOfficers({
        api: API_FIELD_OFFICERS,
        params: {},
      });
    }, []);

    useEffect(() => {
      if (form?.loan_type) {
        getInterestRates({
          api: "configurables/interest-configurations/",
          params: {
            purpose: form?.loan_type || "",
            page_size: 50,
          },
        });
      }
    }, [form?.loan_type]);

    useEffect(() => {
      if (shouldFetchNumberPlate() && numberPlate) {
        getNumberPlates({
          api: `${API_LOAN_APPLICATIONS}`,
          params: { number_plate: numberPlate, status: DISBURSED },
        });
      }
    }, [numberPlate, form.application_type]);

    useEffect(() => {
      if (form.loan_type) {
        const selectedInterest = interests?.results.find(
          // TODO: Fix this
          (item) => item.purpose === form.loan_type,
          // (item) => item.id === form.loan_type,
        );
        if (selectedInterest) {
          setInterestPercent(selectedInterest?.interest);
        }
      }
    }, [form?.loan_type]);

    const getNumberPlateOptions = (
      data: any[] | Record<string, any>,
      key?: string | undefined | null,
    ) => {
      const results = Array.isArray(data) ? data : data?.results || [];
      return results.length
        ? results.map((item: any) => ({
            id: key ? item[key] : item.id,
            name: `${item?.number_plate} (${item?.first_name} ${item?.last_name})`.trim(),
            data: item,
          }))
        : [];
    };

    const sortedRates = interestRates?.results.sort(
      (a, b) => a.interest - b.interest,
    );

    return {
      interests: getResultOptions(interests, "purpose", "purpose"),
      interest_rates: getResultOptions(sortedRates, "interest", "interest"),
      number_plates: getNumberPlateOptions(numberPlates, "number_plate"),
      inventories: getResultOptions(inventories, "name", "id"),
      field_officers: getResultOptions(fieldOfficers, "first_name", "id"),
    };
  };

  const options = {
    ...getConfigOptions(),
    ...loanPeriodOptions,
    // ...location?.options,
  };
  const draftButtonOnClick = (payload: any, id: number | null) => {
    const preppedPayload = getPreppedPayload(payload);
    return {
      api: API_LOAN_APPLICATIONS_DRAFTS,
      id,
      type: FORM_DATA,
      params: preppedPayload,
    };
  };

  return {
    sections,
    redirect,
    options,
    draftButtonOnClick,
    draftRedirect,
    draftsApi,
  };
};

export function submitLoanApplication(payload: any, id: number | null) {
  const preppedPayload = getPreppedPayload(payload);
  return {
    api: API_LOAN_APPLICATIONS,
    id,
    type: FORM_DATA,
    params: preppedPayload,
  };
}

export const useLoanPeriodFields = ({ form, updateFormValue }) => {
  const [no_months, setNoMonths] = useState("");

  useEffect(() => {
    updateFormValue("number_of_months", no_months);
  }, [no_months]);

  const fields = [
    {
      field_name: "loan_validity_period",
      type: "select",
      label: "Loan Validity Period (Years)",
      props: { required: true, smallWidth: true },
      options_name: "loan_validity_periods",
    },
    {
      field_name: "number_of_months",
      type: "number",
      label: "Number of Months",
      props: { disabled: true, smallWidth: true },
      visible: form?.loan_validity_period !== undefined,
    },
    {
      field_name: "amortization_method",
      type: "select",
      label: "Armotization Method",
      props: { required: true, smallWidth: true },
      options_data: [{ id: "straight-line", name: "Straight Line" }],
    },
    {
      field_name: "repayment_period",
      type: "select",
      label: "Loan Repayment Period",
      options_data: [
        { id: "Monthly", name: "Monthly" },
        { id: "Weekly", name: "Weekly" },
        { id: "Bi-weekly", name: "Bi-Weekly" },
      ],
      props: { required: true, smallWidth: true },
    },
  ];

  const getConfigOptions = () => {
    const { loadData: getLoanValidityPeriods, data: loanValidityPeriods } =
      useGet();

    useEffect(() => {
      getLoanValidityPeriods({
        api: API_PERIOD_CONFIGURABLES,
        params: { page_size: 50 },
      });
    }, []);

    useEffect(() => {
      if (form.loan_validity_period) {
        const selectedPeriod = loanValidityPeriods?.results.find(
          (item) => item.id === form.loan_validity_period,
        );
        if (selectedPeriod) {
          setNoMonths(selectedPeriod?.number_of_months);
        }
      }
    }, [form?.loan_validity_period]);

    const sortedLoanValidityPeriods = loanValidityPeriods?.results.sort(
      (a, b) => a.validity_period - b.validity_period,
    );

    return {
      loan_validity_periods: getResultOptions(
        sortedLoanValidityPeriods,
        "validity_period",
        null,
      ),
    };
  };

  return { fields, options: getConfigOptions() };
};
